import { ArrowRightIcon, PeopleIcon, SettingsIcon } from '@blueprinthq/joy'
import {
  Box,
  Button,
  type ButtonProps,
  Flex,
  forwardRef,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Tooltip,
  VStack
} from '@chakra-ui/react'
import {
  CompassOutlined as Compass,
  EndSession,
  LightBulb,
  Mic,
  MicOff,
  SessionAgenda as SessionAgendaIcon,
  SessionNotes as SessionNotesIcon,
  ShareScreen,
  Spotlight,
  StopShareScreen,
  VideoCamera,
  VideoCameraOff
} from '@components/icons'
import {
  useAudioTrack,
  useDaily,
  useLocalSessionId,
  useParticipantIds,
  useParticipantProperty,
  useScreenShare,
  useVideoTrack,
  useWaitingParticipants
} from '@daily-co/daily-react'
import { useAudioCapture, useExperienceManager, usePanelManager } from '@hooks'
import { trackEvent } from '@lib/clinician-tracking'
import { useStoreActions, useStoreState } from 'easy-peasy'
import React from 'react'
import type { StoreModel } from 'src/store/types'
import { WarningTooltipExtension } from '../shared/warning-tooltip'
import { useActiveTelehealthSession } from '../telehealth/context/active-telehealth-session.context'

export const ToolbarButton = forwardRef<ButtonProps, 'button'>((props, ref) => {
  return (
    <Button
      ref={ref}
      variant="transparent"
      p="8px"
      m="0px"
      h="54px"
      minW={{
        base: '48px',
        md: '64px'
      }}
      borderRadius="8px"
      style={{ marginInlineStart: 0 }}
      _hover={{ bg: 'pale_gray' }}
      _loading={{ bg: 'white' }}
      _focus={{ outline: 'none' }}
      {...props}
    />
  )
})

type RecordButtonProps = {
  isRecordingLoading: boolean
  hasStartedRecording: boolean
  startRecording: () => void
  isDictation?: boolean
}

const MicIcon = (props: any) => <Mic fill="#282828" {...props} />

const AudioActivity = ({ isRecording }: { isRecording: boolean }) => {
  const { audioLevel } = useStoreState((state: StoreModel) => state.volume)

  return <MicIcon dy={isRecording ? 1 - audioLevel : null} filterId="footer" />
}

export const RecordButton = ({
  isRecordingLoading,
  hasStartedRecording,
  startRecording,
  isDictation = false
}: RecordButtonProps) => {
  const { isExtension, isWidget } = useExperienceManager()
  const {
    isReady,
    hasMicAccess,
    isMuted,
    toggleMute,
    showMicWarning,
    setActiveWarning,
    isRecording
  } = useAudioCapture()

  const renderMicIcon = () => {
    if (!isMuted) {
      return <AudioActivity isRecording={isRecording} />
    }
    return <MicOff fill="#EB5164" />
  }

  const handleRecordingClick = () => {
    if (isReady && hasStartedRecording) {
      toggleMute()
    } else {
      startRecording()
    }
  }

  const getRecordButtonText = () => {
    if (isMuted) {
      return 'Resume Recording'
    } else if (!hasStartedRecording) {
      return isDictation ? 'Record' : 'Record'
    } else {
      return 'Pause Recording'
    }
  }

  return (
    <Tooltip
      isOpen={showMicWarning}
      hasArrow
      pointerEvents="all"
      label={
        <WarningTooltipExtension
          message={
            <Text fontSize="16px" color="#FFF">
              Hey, <b>we're not hearing any audio</b> from your microphone. Take
              a moment to check your settings.
            </Text>
          }
          onClose={() => setActiveWarning(null)}
        />
      }
      zIndex="tooltip"
      maxW={isExtension || isWidget ? '345px' : '438px'}
      bg="black"
      placement="top"
      px={2}
      py={4}
      border="1px solid white"
      sx={{
        '--popper-arrow-shadow-color': 'white'
      }}
      borderRadius="16px"
    >
      <ToolbarButton
        position="relative"
        onClick={() => {
          trackEvent('Session -> Clicked Start Recording')
          handleRecordingClick()
        }}
        isDisabled={!hasMicAccess || isRecordingLoading}
        data-click-text={getRecordButtonText()}
      >
        <Stack>
          <HStack>
            <Flex flex={1} justifyContent="center">
              {renderMicIcon()}
            </Flex>
          </HStack>
          <Text fontSize="10px" style={{ marginTop: 0 }}>
            {getRecordButtonText()}
          </Text>
        </Stack>
      </ToolbarButton>
    </Tooltip>
  )
}

export const EndSessionButton = ({
  onClick,
  text
}: {
  onClick: () => void
  text?: string
}) => {
  return (
    <ToolbarButton
      id="pendo-end-session"
      onClick={() => {
        trackEvent('Session -> Clicked End Session')
        onClick()
      }}
      data-click-text="End Session"
    >
      <VStack>
        <EndSession />
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          {text || 'End Session'}
        </Text>
      </VStack>
    </ToolbarButton>
  )
}

export const SettingsButton = ({
  onClick,
  icon,
  showTabMicWarning,
  setActiveWarning
}: {
  onClick: () => void
  icon?: React.ReactNode
  showTabMicWarning?: boolean
  setActiveWarning?: (v: 'mic' | 'tab' | null) => void
}) => {
  return (
    <Tooltip
      isOpen={showTabMicWarning}
      hasArrow
      pointerEvents="all"
      label={
        <WarningTooltipExtension
          message={
            <Text fontSize="16px" color="#FFF">
              Hey, <b>we're not hearing any audio</b> from your{' '}
              <b>client's microphone</b>. Take a moment to check your settings.
            </Text>
          }
          onClose={() => setActiveWarning?.(null)}
        />
      }
      zIndex="tooltip"
      maxW="345px"
      bg="black"
      placement="top"
      px={2}
      py={4}
      border="1px solid white"
      sx={{
        '--popper-arrow-shadow-color': 'white'
      }}
      borderRadius="16px"
    >
      <ToolbarButton
        onClick={() => {
          trackEvent('Session -> Clicked Settings')
          onClick()
        }}
        data-click-text="Settings"
      >
        <VStack>
          {icon || <SettingsIcon />}
          <Text fontSize="10px" style={{ marginTop: 0 }}>
            Settings
          </Text>
        </VStack>
      </ToolbarButton>
    </Tooltip>
  )
}

export const TelehealthSettingsButton = ({
  onClick,
  icon
}: {
  onClick: () => void
  icon?: React.ReactNode
}) => {
  const { setIsLayoutSettingsModalOpen } = useActiveTelehealthSession()
  return (
    <Menu>
      <MenuButton
        as={ToolbarButton}
        onClick={() => {
          trackEvent('click:telehealth-session.settings')
        }}
        data-click-text="Settings"
      >
        <VStack>
          {icon || <SettingsIcon />}
          <Text fontSize="10px" style={{ marginTop: 0 }}>
            Settings
          </Text>
        </VStack>
      </MenuButton>
      <MenuList>
        <MenuItem
          icon={<Spotlight />}
          onClick={() => setIsLayoutSettingsModalOpen(true)}
          data-click-text="Change layout"
        >
          Change layout
        </MenuItem>
        <MenuItem
          icon={<SettingsIcon />}
          onClick={onClick}
          data-click-text="Open settings"
        >
          Settings
        </MenuItem>
      </MenuList>
    </Menu>
  )
}

const ParticipantItem = ({
  participantId,
  isLocal
}: {
  participantId: string
  isLocal: boolean
}) => {
  const participantName = useParticipantProperty(participantId, 'user_name')

  const daily = useDaily()

  return (
    <Flex gap="xsmall" alignItems="center">
      <Text>{isLocal ? 'You' : participantName}</Text>
      {!isLocal && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            daily?.updateParticipant(participantId, {
              eject: true
            })
          }}
          data-click-text="Remove participant"
        >
          Kick
        </Button>
      )}
    </Flex>
  )
}

export const ParticipantsButton = ({
  onClick,
  icon,
  isActive
}: {
  onClick: () => void
  icon?: React.ReactNode
  isActive: boolean
}) => {
  return (
    <Box position="relative" display="inline-block">
      <ToolbarButton
        onClick={() => {
          trackEvent('Session -> Clicked Participants')
          onClick()
        }}
        data-click-text="People"
      >
        <VStack>
          {icon || <PeopleIcon fill={isActive ? '#2D54E8' : '#282828'} />}
          <Text fontSize="10px" style={{ marginTop: 0 }}>
            People
          </Text>
        </VStack>
      </ToolbarButton>
    </Box>
  )
}

export const SessionPlanButton = () => {
  const { isEvidenceBasedCareEnabled } = useExperienceManager()
  const { isSessionAgendaOpen, toggleSessionAgenda } = usePanelManager()
  const { user } = useStoreState((state: any) => state.auth)
  const openEbcMarketingModal = useStoreActions(
    (actions: any) => actions.modals.ebcMarketingModal.openModal
  )
  return (
    <ToolbarButton
      onClick={
        isEvidenceBasedCareEnabled
          ? toggleSessionAgenda
          : () => openEbcMarketingModal({ clinicId: user.clinicId })
      }
      data-click-text="Session plan"
    >
      <VStack>
        <SessionAgendaIcon fill={isSessionAgendaOpen ? '#2D54E8' : '#282828'} />
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          Session plan
        </Text>
      </VStack>
    </ToolbarButton>
  )
}

export const PrivateNotesButton = () => {
  const { toggleSessionNotes, isSessionNotesOpen } = usePanelManager()
  return (
    <ToolbarButton
      onClick={toggleSessionNotes}
      data-click-text="Psychotherapy note"
    >
      <VStack>
        <SessionNotesIcon fill={isSessionNotesOpen ? '#2D54E8' : '#282828'} />
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          Psychotherapy note
        </Text>
      </VStack>
    </ToolbarButton>
  )
}

export const DictationTipsButton = () => {
  const { toggleDictationTips, isDictationTipsOpen } = usePanelManager()
  return (
    <ToolbarButton onClick={toggleDictationTips} data-click-text="Tips">
      <VStack>
        <LightBulb fill={isDictationTipsOpen ? '#2D54E8' : '#282828'} />
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          Tips
        </Text>
      </VStack>
    </ToolbarButton>
  )
}

export const AssistButton = () => {
  const { isAssistOpen, toggleAssist } = usePanelManager()

  return (
    <ToolbarButton
      onClick={() => {
        toggleAssist()
      }}
      data-click-text={isAssistOpen ? 'Turn off assist' : 'Turn on assist'}
    >
      <VStack>
        <Compass fill={isAssistOpen ? '#2D54E8' : '#282828'} />
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          Assist
        </Text>
      </VStack>
    </ToolbarButton>
  )
}

export const TelehealthAssistButton = ({
  isActive,
  onClick
}: {
  isActive: boolean
  onClick: () => void
}) => {
  return (
    <ToolbarButton
      onClick={onClick}
      data-click-text={isActive ? 'Turn off assist' : 'Turn on assist'}
    >
      <VStack>
        <Compass fill={isActive ? '#2D54E8' : '#282828'} />
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          Assist
        </Text>
      </VStack>
    </ToolbarButton>
  )
}

export const ExtensionAssistButton = () => {
  const { isMobileAssistOpen, toggleMobileAssist } = usePanelManager()

  return (
    <ToolbarButton onClick={toggleMobileAssist} data-click-text="Assist">
      <VStack>
        <Compass fill={isMobileAssistOpen ? '#2D54E8' : '#282828'} />
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          Assist
        </Text>
      </VStack>
    </ToolbarButton>
  )
}

export const DailyCameraButton = () => {
  const daily = useDaily()

  const localId = useLocalSessionId()
  const camTrack = useVideoTrack(localId)

  const handleToggleCam = () => {
    daily?.setLocalVideo(camTrack.isOff)
  }

  return (
    <ToolbarButton
      onClick={handleToggleCam}
      data-click-text={camTrack.isOff ? 'Turn on camera' : 'Turn off camera'}
    >
      <VStack>
        {camTrack.isOff ? <VideoCameraOff /> : <VideoCamera width="24px" />}
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          {camTrack.isOff ? 'Turn on' : 'Turn off'}
        </Text>
      </VStack>
    </ToolbarButton>
  )
}

export const DailyMicButton = () => {
  const daily = useDaily()
  const localId = useLocalSessionId()
  const micTrack = useAudioTrack(localId)

  const handleToggleMic = () => {
    daily?.setLocalAudio(micTrack.isOff)
  }

  const Icon = micTrack.isOff ? <MicOff /> : <Mic />

  return (
    <ToolbarButton
      onClick={handleToggleMic}
      data-click-text={micTrack.isOff ? 'Unmute microphone' : 'Mute microphone'}
    >
      <VStack>
        {Icon}
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          {micTrack.isOff ? 'Unmute' : 'Mute'}
        </Text>
      </VStack>
    </ToolbarButton>
  )
}

export const DailyLeaveCallButton = () => {
  const daily = useDaily()
  const handleLeaveCall = () => {
    daily?.leave()
  }
  return (
    <ToolbarButton onClick={handleLeaveCall} data-click-text="Leave call">
      <VStack>
        <ArrowRightIcon fill="#282828" />
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          Leave call
        </Text>
      </VStack>
    </ToolbarButton>
  )
}

export const DailyShareScreenButton = () => {
  const { isSharingScreen, startScreenShare, stopScreenShare } =
    useScreenShare()
  const handleToggleScreenShare = () => {
    if (isSharingScreen) {
      stopScreenShare()
    } else {
      startScreenShare({
        screenVideoSendSettings: 'motion-optimized'
      })
    }
  }

  return (
    <ToolbarButton
      onClick={handleToggleScreenShare}
      data-click-text={isSharingScreen ? 'Stop sharing screen' : 'Share screen'}
    >
      <VStack>
        {isSharingScreen ? <StopShareScreen fill="#EB5164" /> : <ShareScreen />}
        <Text fontSize="10px" style={{ marginTop: 0 }}>
          {isSharingScreen ? 'Stop share' : 'Share'}
        </Text>
      </VStack>
    </ToolbarButton>
  )
}
